import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  Avatar,
  IconButton,
  Collapse,
  useTheme,
} from '@mui/material';
import {
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  ChatBubbleOutline as MessageSquareIcon,
  KeyboardArrowDown as ChevronDownIcon,
  KeyboardArrowUp as ChevronUpIcon,
  Send as SendIcon,
  Forum as ForumIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { forumAPI } from '../../services/api';
import { toast } from 'react-toastify';
import Button from '../UIKit/Button';

const LessonForumSection = ({ lessonId, lessonTitle }) => {
  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation(['lessons', 'common']);
  const theme = useTheme();
  
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state
  const [newTopicTitle, setNewTopicTitle] = useState('');
  const [newTopicContent, setNewTopicContent] = useState('');
  const [isSubmittingTopic, setIsSubmittingTopic] = useState(false);
  
  // Reply state
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  
  // Expanded topics
  const [expandedTopics, setExpandedTopics] = useState({});

  // Fetch topics for this lesson
  const fetchTopics = useCallback(async () => {
    if (!lessonId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await forumAPI.getAllTopics({
        lessonId: lessonId,
        page: 1,
        limit: 10,
        sortBy: 'newest'
      });
      
      setTopics(response.data.topics || []);
    } catch (err) {
      console.error('Error fetching lesson topics:', err);
      setError(t('lessons:forum.errors.loadError'));
      setTopics([]);
    } finally {
      setLoading(false);
    }
  }, [lessonId, t]);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  // Handle topic submission
  const handleSubmitTopic = useCallback(async () => {
    if (!newTopicTitle.trim() || !newTopicContent.trim()) {
      toast.error(t('lessons:forum.errors.titleAndContentRequired'));
      return;
    }

    if (!isAuthenticated) {
      toast.error(t('lessons:forum.errors.loginRequired'));
      return;
    }

    setIsSubmittingTopic(true);
    try {
      const topicData = {
        title: newTopicTitle,
        content: newTopicContent,
        lesson: lessonId,
        type: 'discussion',
      };

      await forumAPI.createTopic(topicData);
      toast.success(t('lessons:forum.success.topicCreated'));
      
      // Reset form
      setNewTopicTitle('');
      setNewTopicContent('');
      
      // Refresh topics
      fetchTopics();
    } catch (error) {
      console.error('Error creating topic:', error);
      toast.error(t('lessons:forum.errors.createError'));
    } finally {
      setIsSubmittingTopic(false);
    }
  }, [newTopicTitle, newTopicContent, isAuthenticated, lessonId, t, fetchTopics]);

  // Handle reply submission
  const handleSubmitReply = useCallback(async (topicId) => {
    if (!replyContent.trim()) {
      toast.error(t('lessons:forum.errors.replyRequired'));
      return;
    }

    if (!isAuthenticated) {
      toast.error(t('lessons:forum.errors.loginRequired'));
      return;
    }

    setIsSubmittingReply(true);
    try {
      const replyData = {
        content: replyContent,
      };

      await forumAPI.addComment(topicId, replyData);
      toast.success(t('lessons:forum.success.replyAdded'));
      
      // Reset form
      setReplyContent('');
      setReplyingTo(null);
      
      // Refresh topics
      fetchTopics();
    } catch (error) {
      console.error('Error adding reply:', error);
      toast.error(t('lessons:forum.errors.replyError'));
    } finally {
      setIsSubmittingReply(false);
    }
  }, [replyContent, isAuthenticated, t, fetchTopics]);

  // Handle like/dislike
  const handleLike = useCallback(async (type, id) => {
    if (!isAuthenticated) {
      toast.error(t('lessons:forum.errors.loginRequired'));
      return;
    }

    try {
      await forumAPI.like(type, id);
      fetchTopics(); // Refresh to get updated counts
    } catch (error) {
      console.error('Error liking:', error);
      toast.error(t('lessons:forum.errors.likeError'));
    }
  }, [isAuthenticated, t, fetchTopics]);

  const handleDislike = useCallback(async (type, id) => {
    if (!isAuthenticated) {
      toast.error(t('lessons:forum.errors.loginRequired'));
      return;
    }

    try {
      await forumAPI.dislike(type, id);
      fetchTopics(); // Refresh to get updated counts
    } catch (error) {
      console.error('Error disliking:', error);
      toast.error(t('lessons:forum.errors.dislikeError'));
    }
  }, [isAuthenticated, t, fetchTopics]);

  // Toggle topic expansion
  const toggleTopicExpansion = (topicId) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }));
  };

  // Format time ago
  const formatTimeAgo = (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) {
      return `${minutes} ${t('common:time.minutesAgo')}`;
    } else if (hours < 24) {
      return `${hours} ${t('common:time.hoursAgo')}`;
    } else {
      return `${days} ${t('common:time.daysAgo')}`;
    }
  };

  // Get user initials
  const getUserInitials = (user) => {
    if (!user) return 'U';
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase() || user.email?.charAt(0).toUpperCase() || 'U';
  };

  // Get avatar color
  const getAvatarColor = (user) => {
    if (!user) return 'primary';
    const colors = ['primary', 'secondary', 'success', 'warning', 'error', 'info'];
    const hash = user._id ? user._id.toString().charCodeAt(0) : 0;
    return colors[hash % colors.length];
  };

  return (
    <Box 
      id="lesson-forum-section"
      sx={{ 
        mt: 4,
        scrollMarginTop: '80px',
      }}
    >
      {/* Section Header */}
      <Typography variant="h5" component="h2" fontWeight="bold" mb={2}>
        {t('lessons:forum.title')}
      </Typography>

      {/* New Topic Form */}
      {isAuthenticated && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              {t('lessons:forum.newTopic')}
            </Typography>
            <TextField
              fullWidth
              value={newTopicTitle}
              onChange={(e) => setNewTopicTitle(e.target.value)}
              placeholder={t('lessons:forum.topicTitlePlaceholder')}
              variant="outlined"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
          </Box>

          <TextField
            fullWidth
            multiline
            rows={4}
            value={newTopicContent}
            onChange={(e) => setNewTopicContent(e.target.value)}
            placeholder={t('lessons:forum.topicContentPlaceholder')}
            variant="outlined"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSubmitTopic}
              disabled={isSubmittingTopic || !newTopicTitle.trim() || !newTopicContent.trim()}
              sx={{
                borderRadius: '8px',
                px: 3,
                py: 1.5,
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                '&.Mui-disabled': {
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              }}
            >
              {isSubmittingTopic 
                ? t('common:actions.saving') 
                : t('lessons:forum.submitTopic')
              }
            </Button>
          </Box>
        </Paper>
      )}

      {/* Topics List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        ) : topics.length > 0 ? (
          topics.map((topic) => (
            <Paper
              key={topic._id}
              elevation={0}
              sx={{
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {/* Avatar */}
                  <Avatar
                    sx={{
                      bgcolor: `${getAvatarColor(topic.author)}.main`,
                      color: `${getAvatarColor(topic.author)}.contrastText`,
                      width: 40,
                      height: 40,
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {getUserInitials(topic.author)}
                  </Avatar>

                  {/* Content */}
                  <Box sx={{ flex: 1 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {topic.author?.firstName} {topic.author?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTimeAgo(topic.createdAt)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Topic Title */}
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      {topic.title}
                    </Typography>

                    {/* Topic Content */}
                    <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                      {topic.content}
                    </Typography>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleLike('topic', topic._id)}
                        sx={{ color: 'text.secondary' }}
                      >
                        <ThumbUpIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="caption" color="text.secondary">
                        {topic.likes?.length || 0}
                      </Typography>

                      <IconButton
                        size="small"
                        onClick={() => handleDislike('topic', topic._id)}
                        sx={{ color: 'text.secondary' }}
                      >
                        <ThumbDownIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="caption" color="text.secondary">
                        {topic.dislikes?.length || 0}
                      </Typography>

                      <IconButton
                        size="small"
                        onClick={() => setReplyingTo(replyingTo === topic._id ? null : topic._id)}
                        sx={{ color: 'text.secondary' }}
                      >
                        <MessageSquareIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="caption" color="text.secondary">
                        {t('lessons:forum.reply')}
                      </Typography>

                      {topic.comments && topic.comments.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={() => toggleTopicExpansion(topic._id)}
                          sx={{ color: 'text.secondary' }}
                        >
                          {expandedTopics[topic._id] ? <ChevronUpIcon fontSize="small" /> : <ChevronDownIcon fontSize="small" />}
                        </IconButton>
                      )}
                      {topic.comments && topic.comments.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {expandedTopics[topic._id] ? t('lessons:forum.hideReplies') : t('lessons:forum.showReplies', { count: topic.comments.length })}
                        </Typography>
                      )}
                    </Box>

                    {/* Reply Form */}
                    <Collapse in={replyingTo === topic._id}>
                      <Box sx={{ mb: 2 }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label={t('lessons:forum.writeReply')}
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          sx={{ mb: 2 }}
                          placeholder={t('lessons:forum.replyPlaceholder')}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleSubmitReply(topic._id)}
                            disabled={isSubmittingReply || !replyContent.trim()}
                            startIcon={<SendIcon />}
                          >
                            {isSubmittingReply ? t('common:actions.submitting') : t('lessons:forum.submitReply')}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setReplyingTo(null);
                              setReplyContent('');
                            }}
                          >
                            {t('common:actions.cancel')}
                          </Button>
                        </Box>
                      </Box>
                    </Collapse>

                    {/* Comments */}
                    <Collapse in={expandedTopics[topic._id]}>
                      <Box sx={{ mt: 2, pl: 2, borderLeft: 2, borderColor: 'divider' }}>
                        {topic.comments && topic.comments.length > 0 ? (
                          topic.comments.map((comment) => (
                            <Box key={comment._id} sx={{ mb: 3, display: 'flex', gap: 2 }}>
                              <Avatar
                                sx={{
                                  bgcolor: `${getAvatarColor(comment.author)}.main`,
                                  color: `${getAvatarColor(comment.author)}.contrastText`,
                                  width: 32,
                                  height: 32,
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                }}
                              >
                                {getUserInitials(comment.author)}
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {comment.author?.firstName} {comment.author?.lastName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatTimeAgo(comment.createdAt)}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  {comment.content}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleLike('comment', comment._id)}
                                    sx={{ color: 'text.secondary' }}
                                  >
                                    <ThumbUpIcon fontSize="small" />
                                  </IconButton>
                                  <Typography variant="caption" color="text.secondary">
                                    {comment.likes?.length || 0}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDislike('comment', comment._id)}
                                    sx={{ color: 'text.secondary' }}
                                  >
                                    <ThumbDownIcon fontSize="small" />
                                  </IconButton>
                                  <Typography variant="caption" color="text.secondary">
                                    {comment.dislikes?.length || 0}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('lessons:forum.noReplies')}
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                </Box>
              </Box>
            </Paper>
          ))
        ) : (
          <Paper
            elevation={0}
            sx={{
              borderRadius: '12px',
              p: 4,
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
              backgroundColor: 'background.paper',
            }}
          >
            <ForumIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('lessons:forum.noTopics')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {!isAuthenticated ? t('lessons:forum.errors.loginRequired') : t('lessons:forum.noTopics')}
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default LessonForumSection;
